<template>
  <div>
    <callout type="warning">Terminated your relationship with a representative? Deactivate them from assigned properties to prevent access to your information.</callout>
    <!-- Add Representative Modal -->
    <add-representative-modal
      :is-active="isModalActive"
      :existing-rep-ids="addedRepIds"
      @close="closeModal"
      @submit="handleRepAdded"
    />

    <!-- Deactivate Representative Modal -->
    <deactivate-rep-modal
      :is-active="isDeactivateModalActive"
      :rep="selectedRepForDeactivation || {}"
      @close="closeDeactivateModal"
      @confirm="handleDeactivateConfirm"
    />

    <div class="box">
      <!-- Header with search and add button -->
      <div class="level">
      <div class="level-left">
        <div class="level-item">
          <h3 class="title">Authorized Representatives</h3>
        </div>
      </div>
      <div class="level-right">
        <div class="level-item">
          <div class="field" style="min-width: 300px; margin-right: 1rem; margin-bottom: 0;">
            <div class="control has-icons-left">
              <input
                v-model="searchTerm"
                class="input"
                type="text"
                placeholder="Search by name, email, or firm..."
                style="height: 40px;"
              />
              <span class="icon is-left">
                <i class="fas fa-search"></i>
              </span>
            </div>
          </div>
        </div>
        <div class="level-item">
          <button class="button is-primary" @click="handleAddRep" style="height: 40px;">
            <span class="icon">
              <i class="fas fa-user-plus"></i>
            </span>
            <span>Add Representative</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="table-container">
      <table class="table is-fullwidth is-hoverable">
        <thead>
          <tr>
            <th>Full Name</th>
            <th>Email</th>
            <th>Firm</th>
            <th>Phone</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="rep in filteredReps"
            :key="rep.rid"
            @click="handleRowClick(rep)"
            style="cursor: pointer;"
          >
            <td>
              <strong>{{ rep.ar_full_name }}</strong>
            </td>
            <td>
              {{ rep.ar_email }}
            </td>
            <td>
              {{ rep.ar_organization_name }}
            </td>
            <td>
              {{ rep.ar_phone_number }}
            </td>
            <td>
              <span
                class="tag is-rounded"
                :style="{
                  backgroundColor: getRepStatusInfo(rep.status).bgColor,
                  color: getRepStatusInfo(rep.status).color,
                  fontWeight: 600,
                }"
              >
                {{ getRepStatusInfo(rep.status).label }}
              </span>
            </td>
            <td @click.stop>
              <button
                v-if="rep.status === 'Active'"
                class="button is-small is-white"
                @click="handleDeactivateRep(rep)"
                title="Deactivate Representative"
                style="color: #cc3000;"
              >
                <span class="icon">
                  <i class="fas fa-trash"></i>
                </span>
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- No results message -->
      <div v-if="filteredReps.length === 0" class="has-text-centered" style="padding: 3rem;">
        <p class="medium-gray">
          <i class="fas fa-user-tie fa-3x" style="color: #cfcfcf; margin-bottom: 1rem;"></i><br>
          No authorized representatives found
        </p>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import {
  getAccessLevelInfo,
  getRepStatusInfo,
} from "../mock-data/authorized-reps.js";
import AddRepresentativeModal from "./AddRepresentativeModal.vue";
import DeactivateRepModal from "./DeactivateRepModal.vue";

export default {
  name: "AuthorizedRepsTable",
  components: {
    AddRepresentativeModal,
    DeactivateRepModal,
  },
  data() {
    return {
      searchTerm: "",
      isModalActive: false,
      isDeactivateModalActive: false,
      selectedRepForDeactivation: null,
    };
  },
  computed: {
    reps() {
      return this.$store.getters['dashboard/authorizedReps'];
    },
    addedRepIds() {
      // Extract related_ar IDs from existing reps to prevent duplicates
      return this.reps.map(rep => rep.related_ar);
    },
    filteredReps() {
      if (!this.searchTerm) {
        return this.reps;
      }
      // Filter authorized reps
      const searchLower = this.searchTerm.toLowerCase();
      return this.reps.filter(rep => {
        return (
          rep.ar_full_name.toLowerCase().includes(searchLower) ||
          rep.ar_email.toLowerCase().includes(searchLower) ||
          rep.ar_organization_name.toLowerCase().includes(searchLower)
        );
      });
    },
  },
  methods: {
    getAccessLevelInfo,
    getRepStatusInfo,
    handleRowClick(rep) {
      console.log("Rep row clicked:", rep);
      this.$emit("row-click", rep);
      // TODO: Navigate to rep detail page or open modal
    },
    editRep(rep) {
      console.log("Edit rep:", rep);
      this.$emit("edit", rep);
      // TODO: Open edit modal
    },
    removeRep(rep) {
      console.log("Remove rep:", rep);
      // Show confirmation before removing
      if (confirm(`Are you sure you want to remove ${rep.ar_full_name} as an authorized representative?`)) {
        this.$emit("remove", rep);
        // TODO: Call API to remove rep
      }
    },
    handleAddRep() {
      this.isModalActive = true;
    },
    closeModal() {
      this.isModalActive = false;
    },
    async handleRepAdded(newArRecord) {
      console.log("Representative added:", newArRecord);

      // Close modal first
      this.closeModal();

      // Refresh the authorized reps list from the backend
      try {
        const userInfo = this.$store.getters['auth/userInfo'];
        const organization_rid = userInfo?.qbUser?.related_organization;

        if (organization_rid) {
          // Fetch updated list of authorized reps
          await this.$store.dispatch('dashboard/fetchAuthorizedReps', organization_rid);
        }
      } catch (error) {
        console.error('Error refreshing authorized reps:', error);
      }

      // Emit event to parent
      this.$emit("rep-added", newArRecord);
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    handleDeactivateRep(rep) {
      this.selectedRepForDeactivation = rep;
      this.isDeactivateModalActive = true;
    },
    closeDeactivateModal() {
      this.isDeactivateModalActive = false;
      this.selectedRepForDeactivation = null;
    },
    async handleDeactivateConfirm(rep) {
      console.log("Representative deactivated:", rep);

      // Close modal first
      this.closeDeactivateModal();

      // Refresh the authorized reps and properties from the backend
      try {
        const userInfo = this.$store.getters['auth/userInfo'];
        const organization_rid = userInfo?.qbUser?.related_organization;

        if (organization_rid) {
          // Fetch updated list of authorized reps and properties in parallel
          await Promise.all([
            this.$store.dispatch('dashboard/fetchAuthorizedReps', organization_rid),
            this.$store.dispatch('dashboard/fetchProperties', { organization_rid })
          ]);
        }
      } catch (error) {
        console.error('Error refreshing data after deactivation:', error);
      }

      // Emit event to parent
      this.$emit("rep-deactivated", rep);
    },
  },
};
</script>

<style scoped>
/* Custom styling to match PhilaUI */
.medium-gray {
  color: #a1a1a1; /* Medium Gray - Philly Style Guide */
}

.buttons {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

/* Responsive - simplify layout on small screens */
@media (max-width: 768px) {
  table th:nth-child(4),
  table td:nth-child(4),
  table th:nth-child(5),
  table td:nth-child(5) {
    display: none;
  }
}
</style>
